import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import LogoImg from "../../assets/icons/Logo2.png";
import { FaBars, FaTimes, FaBell } from "react-icons/fa";
import { TbMessage } from "react-icons/tb";
import { ReactComponent as UserAvatar } from "../../assets/icons/user-avatar.svg";
import { useDispatch, useSelector } from "react-redux";
import { roles } from "../../constants/commonConstants";
import { fetchUnreadThreadCount, logout, switchRole } from "../../store";
import ProfileImage from "../Common/ProfileImage";
import { useThunk } from "../../hooks/useThunk";
import { localStorageUtil } from "../../utils/localStorageUtil";

const Navbar = ({  
  profileDropDownLinkStyle = "text-content leading-6 hover:text-primary block transition-colors duration-150",
}) => {

  const { isAuthenticated, user, role, availableRoles } = useSelector(
    (state) => state.auth
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef(null);

  //fetch unread thread count
  const [
    doFetchUnreadThreadCount,
    isFetchUnreadThreadCount,
    fetchUnreadThreadCountError,
  ] = useThunk(fetchUnreadThreadCount);
  const [unreadThreadCount, setUnreadThreadCount] = useState();

  const loggedUser = localStorageUtil.get("auth");

  // Close menu and profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMenuOpen, isProfileOpen]);

  useEffect(() => {
    const fetchUnreadThreadCount = async () => {
      if (!loggedUser?.isAuthenticated) return;

      const response = await doFetchUnreadThreadCount();
      const threadCount = response?.response?.data[0]?.count;

      if (response?.response?.data?.length === 0) {
        setUnreadThreadCount(0);
      } else {
        setUnreadThreadCount(response?.response?.data[0]?.count);
      }

      console.log("unread Thread count", threadCount);
    };

    fetchUnreadThreadCount();
  }, [location]);

  const NavButton = ({
    children,
    to,
    variant = "default",
    className = "",
    type,
  }) => {
    const baseStyles =
      "font-roboto font-medium text-level-5 leading-6 transition-colors duration-200";
    const variants = {
      default:
        "text-dark hover:bg-gray-100 px-4 md:max-ml:px-2 py-2 rounded-lg",
      outline:
        "border border-primary text-primary-text hover:bg-primary-light px-8 py-2 rounded-[12px]",
      primary: "bg-primary text-white hover:bg-primary/90 rounded-lg",
    };

    if (type === "button") {
      return (
        <button
          className={`${baseStyles} ${variants[variant]} ${className}`}
          type="button"
          onClick={to}
        >
          {children}
        </button>
      );
    } else {
      return (
        <Link
          to={to}
          className={`${baseStyles} ${variants[variant]} ${className}`}
        >
          {children}
        </Link>
      );
    }
  };

  const handleRoleToggle = () => {
    // Switch between 'client' and 'tasker'
    const newRole = role === roles?.TASKER ? "USER" : "TASKER";
    console.log("newRole", newRole);
    dispatch(switchRole(newRole));

    // Redirect based on new role
    navigate(newRole === roles?.TASKER ? "/service-pro" : "/client");
  };

  const handleCloseDropdown = () => {
    setIsProfileOpen(false);
  };

  const ProfileDropdown = () => {
    return (
      <div
        className={`absolute right-0 mt-2 w-60 h-auto flex items-start flex-col gap-4 bg-white rounded-3xl shadow-lg py-6 pl-6 border font-inter font-medium text-level-6 border-gray-200
      transform transition-all duration-200 ease-in-out origin-top ${
        isProfileOpen
          ? "opacity-100 scale-100"
          : "opacity-0 scale-95 pointer-events-none"
      }
      `}
      >
        <button
          onClick={() => {
            handleRoleToggle();
            handleCloseDropdown();
          }}
          className={profileDropDownLinkStyle}
        >
          {role === roles?.USER ? "Become a Service Pro" : "Become a Client"}
        </button>

        {role === roles?.USER ? (
          <>
            <Link
              to="/client/profile"
              onClick={handleCloseDropdown}
              className={profileDropDownLinkStyle}
            >
              My Profile
            </Link>
            <Link
              to="/client/my-posts"
              onClick={handleCloseDropdown}
              className={profileDropDownLinkStyle}
            >
              My Posts
            </Link>
            <Link
              to="/client/order-history"
              onClick={handleCloseDropdown}
              className={profileDropDownLinkStyle}
            >
              Order Details
            </Link>
            <Link
              to="/chat"
              onClick={handleCloseDropdown}
              className={profileDropDownLinkStyle}
            >
              Chat
            </Link>
          </>
        ) : (
          <>
            {availableRoles?.includes("TASKER") ? (
              <>
                <Link
                  to="/service-pro/tasker-profile"
                  onClick={handleCloseDropdown}
                  className={profileDropDownLinkStyle}
                >
                  My Profile
                </Link>
                <Link
                  to="/service-pro/my-jobs"
                  onClick={handleCloseDropdown}
                  className={profileDropDownLinkStyle}
                >
                  My Jobs
                </Link>
                <Link
                  to="/service-pro/my-wallet"
                  onClick={handleCloseDropdown}
                  className={profileDropDownLinkStyle}
                >
                  My Wallet
                </Link>
                <Link
                  to="/chat"
                  onClick={handleCloseDropdown}
                  className={profileDropDownLinkStyle}
                >
                  Chat
                </Link>
              </>
            ) : (
              <>
                {
                  // If user has requested to be a tasker and still not verified then do not show the link
                  user?.taskerRequestedAndStillNotVerified !== true && (
                    <Link
                      to="/tasker-signup"
                      onClick={handleCloseDropdown}
                      className={profileDropDownLinkStyle}
                    >
                      Register as Service Pro
                    </Link>
                  )
                }
                <Link
                  to="/chat"
                  onClick={handleCloseDropdown}
                  className={profileDropDownLinkStyle}
                >
                  Chat
                </Link>
              </>
            )}
          </>
        )}

        <button
          onClick={() => dispatch(logout())}
          className="block text-content leading-6 hover:text-[#E35656] transition-colors duration-150"
        >
          Logout
        </button>
      </div>
    );
  };

  return (
    <nav className="fixed px-[20px] md:px-[40px] lg:px-[100px] top-0 left-0 right-0 z-40">
      <div className="blur-overlay"></div>
      <div className="border-b border-[#DDE1E6] rounded-3xl mt-6 px-4 md:px-10 py-4 mx-auto bg-white">
        <div className="relative flex items-center justify-between">
          {/* Logo */}
          <div className="flex-shrink-0 w-[148px] h-[28px]">
            <Link
              to={role === roles?.USER ? "/client" : "/service-pro"}
              className="w-[128.4px] xs:w-[148.4px] h-[21.3px] relative z-50"
            >
              <img
                src={LogoImg}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </Link>
          </div>

          <div className="flex items-center gap-0 xs:gap-3 md:hidden">
            {/* Small Screen Avatar and Bell Icons */}
            {isAuthenticated && (
              <div className="flex items-center gap-1 xs:gap-4 md:hidden">
                <Link className="relative z-50" to="/chat">
                  <div className="xs:h-8 xs:w-8 w-7 h-7">
                    <TbMessage className="text-dark lg:text-2xl h-full w-full opacity-70" />
                  </div>
                  {unreadThreadCount > 0 && (
                    <span className="absolute -top-0 -right-1 min-w-[15px] h-[15px] px-[5px] text-[8px] leading-[18px] xs:min-w-[20px] xs:h-[20px] xs:px-[5px] xs:text-[10px] xs:leading-[18px] xs:-right-3 xs:-top-1 text-white bg-primary rounded-full flex items-center justify-center">
                      {unreadThreadCount > 99 ? "99+" : unreadThreadCount}
                    </span>
                  )}
                </Link>
                <div ref={dropdownRef} className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsProfileOpen(!isProfileOpen);
                    }}
                    className={`relative z-50 w-7 h-7 xs:w-9 xs:h-9 rounded-full overflow overflow-hidden transition-transform duration-200 hover:scale-105`}
                  >
                    {isAuthenticated && user && (
                      <ProfileImage
                        profilePicture={user.profilePicture}
                        firstName={user.firstName}
                        fontSize="text-[15px] xs:text-[18px]"
                      />
                    )}
                  </button>
                  {isProfileOpen && <ProfileDropdown />}
                </div>
              </div>
            )}

            {/* Hamburger Menu */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              aria-label="Toggle menu"
              className="md:hidden relative z-50 text-2xl text-dark focus:outline-none p-2"
            >
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>

          {/* Desktop Navigation - Using flexbox with flex-grow for center section */}
          <div className="hidden md:flex flex-grow justify-between items-center">
            {/* Left spacer to help with centering */}
            <div className="hidden xl:flex flex-grow-0 w-24"></div>

            {/* Centered Navigation Links - Now using flex instead of absolute positioning */}
            <div className="flex items-center justify-center flex-shrink-0 gap-1 lg:gap-4 lg:max-xl:ml-10">
              <NavButton
                to="/about"
                className="whitespace-nowrap text-xs lg:text-base px-2 lg:px-0 xl:px-4"
              >
                About Us
              </NavButton>
              <NavButton
                to="/contact"
                className="whitespace-nowrap text-xs lg:text-base px-2 lg:px-0 xl:px-4"
              >
                Contact Us
              </NavButton>
              {role === roles?.USER ? (
                <NavButton
                  to="/service-pro/explore-jobs"
                  className="whitespace-nowrap text-xs lg:text-base px-2 lg:px-0 xl:px-4"
                >
                  Find Job
                </NavButton>
              ) : (
                <NavButton
                  to="/client/post-job"
                  className="whitespace-nowrap text-xs lg:text-base px-2 lg:px-0 xl:px-4"
                >
                  Post a Job
                </NavButton>
              )}
            </div>

            {/* Right-aligned auth buttons */}
            <div className="flex-shrink-0 flex items-center gap-2 lg:gap-4">
              {isAuthenticated ? (
                <div className="flex items-center gap-3 lg:gap-6 ">
                  <Link className="relative z-50" to="/chat">
                    <TbMessage
                      size={32}
                      className="text-dark lg:text-2xl opacity-70"
                    />
                    {unreadThreadCount > 0 && (
                      <span className="absolute -top-1 -right-3 min-w-[20px] h-[20px] px-[5px] text-[10px] leading-[18px] text-white bg-primary rounded-full flex items-center justify-center">
                        {unreadThreadCount > 99 ? "99+" : unreadThreadCount}
                      </span>
                    )}
                  </Link>
                  <div ref={dropdownRef} className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsProfileOpen(!isProfileOpen);
                      }}
                      className={`relative z-50 w-9 h-9 lg:w-10 lg:h-10 rounded-full overflow-hidden transition-transform duration-200 hover:scale-105`}
                    >
                      {isAuthenticated && user && (
                        <ProfileImage
                          profilePicture={user.profilePicture}
                          firstName={user?.firstName}
                          fontSize="text-[17px] lg:text-[18px]"
                        />
                      )}
                    </button>
                    {isProfileOpen && <ProfileDropdown />}
                  </div>
                </div>
              ) : (
                <div className="flex items-center">
                  <NavButton
                    to="/signin"
                    className="text-primary-text text-xs lg:text-base whitespace-nowrap px-2 lg:px-4"
                  >
                    Sign In / Sign Up
                  </NavButton>
                  <NavButton
                    to={handleRoleToggle}
                    variant="outline"
                    className="font-semibold text-xs lg:text-base whitespace-nowrap px-2 lg:px-6 md:max-lg:px-5"
                    type="button"
                  >
                    {role === roles.USER
                      ? "Become a Service Pro"
                      : "Become a Client"}
                  </NavButton>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <div
            className={`
              fixed md:hidden inset-0 top-[73px] bg-white
              transition-all duration-300 ease-in-out overflow-y-hidden 
              ${
                isMenuOpen
                  ? "opacity-100 visible h-screen"
                  : "opacity-0 invisible"
              }
              flex flex-col items-center gap-6
              p-6 overflow-y-auto
              border-t-0 border-[#DDE1E6]
            `}
          >
            {/* Mobile Navigation Links */}
            <div className="flex flex-col items-center gap-4">
              <NavButton to="/about">About Us</NavButton>
              <NavButton to="/contact">Contact Us</NavButton>
              <NavButton
                to={
                  role === roles?.USER
                    ? "/client/post-job"
                    : "/service-pro/explore-jobs"
                }
              >
                {role === roles?.USER ? "Post a Jobs" : "Find Job"}
              </NavButton>
            </div>

            {/* Mobile-only Sign In */}
            {!isAuthenticated && (
              <div className="w-full flex flex-col gap-4 mt-4">
                <NavButton
                  to="/signin"
                  className="text-primary-text w-full text-center"
                >
                  Sign In / Sign Up
                </NavButton>
                <NavButton
                  to={handleRoleToggle}
                  variant="outline"
                  className="font-semibold w-full text-center"
                  type="button"
                >
                  {role === roles.USER
                    ? "Become a Service Pro"
                    : "Become a Client"}
                </NavButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
